import { useMemo } from 'react';

export const usePortfolioData = (trades, dbPrices, dividends = [], cashTransactions = [], portfolioType = 'BROKERAGE') => {
  return useMemo(() => {
    const isCustody = portfolioType === 'CUSTODY_CDC';
    const holdingsMap = {};
    let totalRealizedPnL = 0;
    let totalFeesPaid = 0;
    let totalBuyCost = 0;
    let totalSellReturn = 0;
    const realizedTradesHistory = [];
    const sortedTrades = [...trades].sort((a,b) => new Date(a.date) - new Date(b.date));

    sortedTrades.forEach(trade => {
      if (!holdingsMap[trade.symbol]) {
        holdingsMap[trade.symbol] = { symbol: trade.symbol, quantity: 0, totalCost: 0, totalFees: 0, portfolioQuantities: {} };
      }
      const holding = holdingsMap[trade.symbol];
      const pId = trade.portfolioId ? (typeof trade.portfolioId === 'object' ? trade.portfolioId._id : trade.portfolioId) : 'unknown';
      if (!holding.portfolioQuantities[pId]) holding.portfolioQuantities[pId] = 0;
      const tradeTotalCost = trade.totalCost || ((trade.totalFee || 0) + (trade.psxLaga || 0) + (trade.secpLaga || 0) + (trade.nccpl || 0) + (trade.sst || 0) + (trade.cdcFee || 0));
      const fee = tradeTotalCost;
      totalFeesPaid += fee;
      holding.totalFees += fee;
      
      if (trade.tradeType === 'Buy') {
        holding.quantity += trade.quantity;
        holding.portfolioQuantities[pId] += trade.quantity;
        holding.totalCost += (trade.quantity * trade.price) + fee;
        totalBuyCost += (trade.quantity * trade.price) + fee;
      } else if (trade.tradeType === 'Sell') {
        totalSellReturn += (trade.quantity * trade.price) - fee;
        if (holding.quantity > 0) {
          const avgCost = holding.totalCost / holding.quantity;
          
          // Calculate Realized PnL (excluding fees, which are tracked separately)
          const tradeRealizedPnL = (trade.price - avgCost) * trade.quantity;
          totalRealizedPnL += tradeRealizedPnL;
          
          realizedTradesHistory.push({
            date: new Date(trade.date).toLocaleDateString(),
            isoDate: trade.date,
            symbol: trade.symbol,
            realizedPnL: tradeRealizedPnL
          });

          holding.quantity -= trade.quantity;
          holding.portfolioQuantities[pId] -= trade.quantity;
          holding.totalCost -= (trade.quantity * avgCost);
        }
      }
    });

    const currentHoldings = Object.values(holdingsMap).filter(h => h.quantity > 0).map(h => {
      const avgCost = h.totalCost / h.quantity;
      const latestPriceData = dbPrices.find(p => p.symbol === h.symbol);
      const currentPrice = latestPriceData ? latestPriceData.price : 0;
      const currentValue = h.quantity * currentPrice;
      const unrealizedPnL = currentValue - h.totalCost;
      const activePortfolioIds = Object.keys(h.portfolioQuantities || {}).filter(id => h.portfolioQuantities[id] > 0);
      
      return {
        ...h,
        portfolioIds: activePortfolioIds,
        avgCost,
        currentPrice,
        currentValue,
        unrealizedPnL,
        sector: latestPriceData && latestPriceData.sector ? latestPriceData.sector : 'Unknown',
        name: latestPriceData && latestPriceData.name ? latestPriceData.name : '',
        lastUpdated: latestPriceData ? latestPriceData.lastUpdated : null
      };
    });

    const totalInvestment = currentHoldings.reduce((sum, h) => sum + h.totalCost, 0);
    const stockCurrentValue = currentHoldings.reduce((sum, h) => sum + h.currentValue, 0);
    const totalPnL = currentHoldings.reduce((sum, h) => sum + h.unrealizedPnL, 0);

    const totalDividends = (dividends || []).reduce((sum, d) => sum + d.amount, 0);
    
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    (cashTransactions || []).forEach(tx => {
      if (tx.type === 'Deposit') totalDeposits += tx.amount;
      if (tx.type === 'Withdrawal') totalWithdrawals += tx.amount;
    });

    // In CDC/Custody accounts, there is no cash ledger/broker cash balance
    const cashBalance = isCustody 
      ? 0 
      : (totalDeposits - totalWithdrawals - totalBuyCost + totalSellReturn);
    const totalCurrentValue = stockCurrentValue;
    const netWorth = isCustody ? stockCurrentValue : (stockCurrentValue + cashBalance);
    // Explicitly subtract total fees from total returns
    const totalReturn = totalRealizedPnL + totalDividends - totalFeesPaid;

    return {
      holdings: currentHoldings,
      realizedHistory: realizedTradesHistory,
      totals: { 
        investment: totalInvestment, 
        currentValue: totalCurrentValue, 
        pnl: totalPnL,
        realizedPnL: totalRealizedPnL,
        dividends: totalDividends,
        fees: totalFeesPaid,
        totalReturn: totalReturn,
        cashBalance: cashBalance,
        stockValue: stockCurrentValue,
        netWorth: netWorth,
        isCustody: isCustody
      }
    };
  }, [trades, dbPrices, dividends, cashTransactions, portfolioType]);
};
